import { DSTooltip } from '@repo/design-system/index'
import { QIcon_arrow_left, QIcon_question_outline } from '@repo/icons/index'
import { useEffect } from 'react'
import { HIGHLIGHT_METRIC, HIGHLIGHT_TAG_MAP } from '../../../../../model/caf.interface'
import { useCafHighlightV5 } from '../../../../../modules/apis/webview/caf'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import { getMetric } from '../../../../../modules/hooks/webview/caf/chart/utilFunction'
import useMapTagToWording from '../../../../../modules/hooks/webview/caf/common/useMapTagToWording'
import { returnMetricItemInfo } from '../../../../../modules/hooks/webview/caf/etc/useHighlightInfo'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { EMOJI_GOOD_BLACK } from '../../../../../modules/svg/svg'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'

// import HighlightDetailListAI from '../../../../webview/caf/highlight/HighlightDetailListAI'

import { useCafSidePopup } from '../../common/CafSidePopup'
import PinIcon from '../../common/HighlightPinIcon'
import HighlightedTitle from '../../common/HighlightedTitle'
import ViewMoreButton from '../../common/ViewMoreButton'
import HighlightDetailListAI from '../../highlight/HighlightDetailListAI'
import HighlightDetailListCorrection from '../../highlight/HighlightDetailListCorrection'
import HighlightDetailListGeneral from '../../highlight/HighlightDetailListGeneral'
import HighlightDetailListPronunciation from '../../highlight/HighlightDetailListPronunciation'
import RingleTip from '../../highlight/RingleTip'
import SidePopupBody from './components/Body'
import useCafRingleTipSidePopup from './useCafRingleTipSidePopup'
import useHighlightDetailSidePopup from './useHighlightDetailSidePopup'

export interface SidePopupBodyProps {
  headerProps: SidePopupHeaderProps
  children: JSX.Element | JSX.Element[] | string | string[]
}
export interface SidePopupHeaderProps {
  title: string | JSX.Element
  onClose: () => void
  SubIcon?: any
  onClickSubIcon?: any
}

const DEFAULT_PROPS = {
  hFull: true,
  bgWhite: true,
  maxW: 512,
  noTopSide: true,
  overlayClose: true,
}
const useHighlightSidePopup = (tag, highlightId, backBtn, onClosePopup = () => null) => {
  const router = useAppRouter()
  const sidePopup = useCafSidePopup()
  const { tab, startDate, endDate, back, lessonId, errorId } = router.query
  useCafRingleTipSidePopup(true)
  useHighlightDetailSidePopup(true, () => {
    router.push(urls.student.newPortal.lessons.reviewDetail.asCafTagAndIdBack(lessonId, tab, tag, highlightId))
  })
  const { t } = useTranslation()
  const { countWordingPerTag, namePerTag, iconPerTag, tooltipWordingPerTag } = useMapTagToWording(true)
  const { data, error, refetch, isLoading, fetchNextPage, isFetchingNextPage } = useCafHighlightV5(+highlightId)
  const tag_num = Number(tag)
  const highlightId_num = Number(highlightId)

  const highlightDetail = {
    ...data?.pages[0],
    details: data?.pages?.reduce((ac, cr) => {
      return [...ac, ...cr.details]
    }, []),
  }

  const getContent = (tag: number) => {
    return (
      <div>
        {tag === HIGHLIGHT_TAG_MAP.ros ||
        tag === HIGHLIGHT_TAG_MAP.repetition ||
        tag === HIGHLIGHT_TAG_MAP.sentence_structure ? (
          <HighlightDetailListAI details={highlightDetail.details} tag={tag} />
        ) : tag === HIGHLIGHT_TAG_MAP.pronunciation ? (
          <HighlightDetailListPronunciation details={highlightDetail.details} highlightId={highlightId_num} tag={tag} />
        ) : tag <= 12 ? (
          // 문법
          <HighlightDetailListCorrection details={highlightDetail.details} tag={tag} />
        ) : (
          // 그 외
          <HighlightDetailListGeneral details={highlightDetail.details} page={highlightDetail.page} />
        )}
        {!!data?.pages[data?.pages.length - 1]?.page?.next && (
          <div className="flex justify-center pb-[88px]">
            <ViewMoreButton handleClick={fetchNextPage} isFetchingNextPage={isFetchingNextPage} />
          </div>
        )}
      </div>
    )
  }
  const goBack = () => {
    if (onClosePopup) {
      onClosePopup()
    } else {
      router.push(urls.student.newPortal.lessons.cafStatistics.asTagBack(tab, startDate, endDate, tag))
    }
  }
  const count = (data) => {
    if (data.highlight_tag === 15 || data.highlight_tag === 16) return data.interval_count
    return data.count
  }
  useEffect(() => {
    if ((tag_num >= 0 && highlightId_num >= 0 && !isLoading && !errorId) || back === 'true') {
      const { is_pinned } = highlightDetail
      const popupId = `caf_highlight_${tag_num}`
      const title =
        count(highlightDetail) > 0
          ? countWordingPerTag(tag_num, count(highlightDetail)).count
          : countWordingPerTag(tag_num).noCount

      const onClose = () => {
        sidePopup.close(popupId)
        onClosePopup && onClosePopup()
      }
      const TagIcon = iconPerTag(tag_num)
      const tooltip = tooltipWordingPerTag(tag)
      sidePopup.show({
        ...DEFAULT_PROPS,
        id: popupId,
        onClose: onClose,
        body: (
          <SidePopupBody
            headerProps={{
              title: (
                <div className="flex flex-row items-center justify-start text-[20px] font-bold text-black">
                  {backBtn && (
                    <QIcon_arrow_left width={24} height={24} className="mr-[8px] cursor-pointer" onClick={goBack} />
                  )}
                  <HighlightedTitle title={title} color={COLORS.PURPLE50} size={20} />
                </div>
              ),
              onClose: onClose,
              SubIcon: (props) => <PinIcon tag={tag_num} isActive={is_pinned} otehrs={props} />,
            }}
          >
            <div className="flex w-[100%] flex-col items-start justify-start pr-[56px]">
              <div className="flx-row mb-[16px] flex items-center justify-start text-[14px] text-gray-600">
                <TagIcon width={20} height={20} className="mr-[4px] [&>*]:fill-current" fill={COLORS.GRAY600} />
                {namePerTag(tag_num)}
                {tooltip && <div className="mx-[10px] mb-[2px] h-[12px] w-[2px] bg-gray-300"></div>}

                {tooltip ? (
                  <div className="flex flex-col items-center">
                    <DSTooltip
                      place="bottom"
                      isMultiline={true}
                      isOpenOnClick={true}
                      triggerComponent={
                        <div className="flex cursor-pointer flex-row items-center justify-start">
                          <span className="mr-[2px] text-[14px] text-gray-600">{t(n_caf.highlight.modal_title)}</span>
                          <QIcon_question_outline fill={COLORS.GRAY600} width={16} height={16} className="mb-[1px]" />
                        </div>
                      }
                      contentComponent={tooltip}
                    />
                  </div>
                ) : null}
              </div>
              {!highlightDetail.active && (
                <div className="mb-[16px] flex w-full items-center justify-center rounded-[6px] border border-gray-200 bg-white p-[16px]">
                  <div className="keep-all flex flex-col items-center justify-start rounded-[6px] text-center text-[14px] font-medium text-gray-700">
                    <img src={EMOJI_GOOD_BLACK} className="mb-[16px]" alt="emoji" />
                    {t(returnMetricItemInfo(getMetric(highlightDetail.highlight_tag) as HIGHLIGHT_METRIC).skeleton_msg)}
                  </div>
                </div>
              )}
              <RingleTip tag={tag} />
              {getContent(tag_num)}
            </div>
          </SidePopupBody>
        ),
        bgColor: COLORS.GRAY50,
      })
    }
  }, [tag_num, highlightId_num, highlightDetail, isLoading])
}

export default useHighlightSidePopup
